// ABOUTME: Protected route wrapper that requires authentication
// ABOUTME: Redirects to auth page if user is not logged in, triggers sync on login

import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { LoaderIcon } from '@/components/icons'
import type { User } from '@/providers'
import { getAuthService } from '@/services/AuthService'
import { getSyncService } from '@/services/SyncService'

interface ProtectedRouteProps {
  children: React.ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation()
  const [user, setUser] = useState<User | null | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const syncTriggered = useRef(false)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authService = getAuthService()
        const currentUser = await authService.getCurrentUser()
        setUser(currentUser)
      } catch (error) {
        console.error('Auth check failed:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()

    // Subscribe to auth state changes
    const authService = getAuthService()
    const unsubscribe = authService.onAuthStateChange((user) => {
      setUser(user)
      // Reset sync flag when user changes
      syncTriggered.current = false
    })

    return () => {
      unsubscribe()
    }
  }, [])

  // Trigger sync when user is authenticated
  useEffect(() => {
    if (user && !syncTriggered.current) {
      syncTriggered.current = true
      const syncService = getSyncService()
      syncService.sync().catch((error) => {
        console.error('Initial sync failed:', error)
      })
    }
  }, [user])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="flex flex-col items-center gap-4">
          <LoaderIcon className="w-8 h-8 animate-spin text-primary-600" />
          <p className="text-sm text-fg-secondary">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    // Redirect to auth page, preserving the intended destination
    return <Navigate to="/auth" state={{ from: location }} replace />
  }

  return <>{children}</>
}
